import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Smartphone, ShieldAlert, Fingerprint, MessageSquare } from 'lucide-react'
import {
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser'
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import type { ConfirmationResult, RecaptchaVerifier } from 'firebase/auth'
import { useI18n } from '../i18n/useI18n'
import { showToast } from './Toast'
import { api, ApiError } from '../services/api'
import { markDeviceApproved, recheckDeviceApproval } from '../hooks/useDeviceApprovalGate'
import { useStore } from '../store/useStore'
import { auth } from '../lib/firebase'
import { getDeviceId } from '../lib/deviceId'
import { toE164Jp, createInvisibleRecaptcha, startPhoneReauth } from '../lib/phoneRecovery'

interface Props {
  open: boolean
  /** 否認済(credential 失効済)。 */
  denied: boolean
  /** 手動再確認(hook が 4s poll しているので無くても自動解除はされる)。 */
  onRecheck: () => void
}

/**
 * 初見デバイス承認 **待機** モーダル (#819 B-main Phase 2b).
 *
 * 未信頼の新端末で RequireAuth が全画面 block。自己復旧の到達導線:
 * - #1105 passkey(端末バインド=single_device のみ heal。同期は #1114 A で恒久明示)
 * - #1107 SMS 復旧(登録番号所持証明。#1114 D で **denied も上書き復帰**可)
 * - #1114 猟予期タイムアウト自動承認(到達可能 trusted 端末が無い構造的
 *   ロックアウトの exit。firstSeen 経過を server が検証)
 * - #1114 退会/ログアウトは gate 中でも常時到達可能(「使えないし削除もできない」解消)
 *
 * out-of-band 通知(grace 自動承認時のメール)で ATO は事後検知に格下げ
 * (memory feedback_ato_detection_out_of_band)。破壊操作(削除)は confirm 必須。
 */
export default function DeviceApprovalWaitingModal({ open, denied, onRecheck }: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { logout, deleteAccount } = useStore()
  const [passkeyBusy, setPasskeyBusy] = useState(false)
  // #1114 A: 同期 passkey で自動信頼不可だった事実を **恒久表示**(トースト脱却)。
  const [syncedFail, setSyncedFail] = useState(false)
  const supported =
    typeof window !== 'undefined' && browserSupportsWebAuthn()

  // #1107 SMS 復旧 (flag ON + 番号登録済みのときだけ)。denied でも表示(#1114 D)。
  const [smsAvailable, setSmsAvailable] = useState(false)
  const [smsStep, setSmsStep] = useState<'idle' | 'phone' | 'code'>('idle')
  const [smsPhone, setSmsPhone] = useState('')
  const [smsCode, setSmsCode] = useState('')
  const [smsBusy, setSmsBusy] = useState(false)
  const confirmRef = useRef<ConfirmationResult | null>(null)
  const verifierRef = useRef<RecaptchaVerifier | null>(null)

  // #1114 猟予期タイムアウト自動承認のカウントダウン(denied では無効)。
  const [graceRemaining, setGraceRemaining] = useState<number | null>(null)
  // #1114+ 到達可能な trusted 端末があり grace 発火しない(承認導線へ誘導)。
  const [graceReachableBlocked, setGraceReachableBlocked] = useState(false)
  const graceInfoRef = useRef<{ firstSeen: number; graceSeconds: number } | null>(null)
  const graceFiredRef = useRef(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    // SMS 復旧の可否(denied でも取得 = D の導線判定)。
    api.getRecoveryPhoneStatus()
      .then((s) => { if (!cancelled) setSmsAvailable(s.enabled === true && s.registered === true) })
      .catch(() => { /* flag OFF / 失敗 = 非表示 */ })
    // grace 情報(denied は自動承認対象外なので取得しない)。#1114+ 4s 間隔で
    // poll し、他に承認済み端末があるか(hasOtherTrusted)を追う。他に承認済み
    // 端末がある間はカウントダウンを出さず「登録済みの端末で承認して」を表示
    // (grace は他に承認済み端末が無い時だけ発火する、という server 判定に UX を一致)。
    if (!denied) {
      const fetchStatus = () => {
        api.getDeviceStatus(getDeviceId())
          .then((s) => {
            if (cancelled) return
            if (typeof s.firstSeen === 'number' && typeof s.graceSeconds === 'number' && s.graceSeconds > 0) {
              graceInfoRef.current = { firstSeen: s.firstSeen, graceSeconds: s.graceSeconds }
            }
            if (s.hasOtherTrusted === true) {
              setGraceReachableBlocked(true)
              setGraceRemaining(null)
            } else if (s.hasOtherTrusted === false) {
              setGraceReachableBlocked(false)
            }
          })
          .catch(() => {})
      }
      fetchStatus()
      const iv = setInterval(fetchStatus, 4000)
      return () => {
        cancelled = true
        clearInterval(iv)
        verifierRef.current?.clear()
        verifierRef.current = null
      }
    }
    return () => {
      cancelled = true
      verifierRef.current?.clear()
      verifierRef.current = null
    }
  }, [open, denied])

  // #1114 grace カウントダウン: firstSeen 経過を毎秒計算、0 で grace-approve 呼出。
  // server が firstSeen 経過を再検証するので client の時計ずれは安全側(not_yet で retry)。
  useEffect(() => {
    // #1114+ 他に承認済み端末がある間はカウントダウン自体を回さない
    // (「120 秒待たせてから承認導線に切替」を廃止し、最初から承認導線を出す)。
    if (!open || denied || graceReachableBlocked) return
    const iv = setInterval(() => {
      const info = graceInfoRef.current
      if (!info) return
      const elapsed = Date.now() / 1000 - info.firstSeen
      const remaining = Math.max(0, Math.ceil(info.graceSeconds - elapsed))
      setGraceRemaining(remaining)
      if (remaining <= 0 && !graceFiredRef.current) {
        graceFiredRef.current = true
        api.graceApproveDevice(getDeviceId())
          .then((res) => {
            if (res.trusted) {
              markDeviceApproved()
              recheckDeviceApproval()
              showToast({ type: 'success', message: t('deviceApproval.graceApproved') })
            } else if (res.result === 'has_reachable_trusted') {
              // #1114+ 到達可能な trusted 端末がある → grace しない。
              // 自動承認を止め、別 trusted 端末のアプリ内承認へ誘導する。
              setGraceReachableBlocked(true)
              setGraceRemaining(null)
              // graceFiredRef は true のまま(再試行しない)。承認は poll が拾う。
            } else {
              // not_yet(時計ずれ)→ 数秒後に再試行を許可。
              graceFiredRef.current = false
            }
          })
          .catch(() => { graceFiredRef.current = false })
      }
    }, 1000)
    return () => clearInterval(iv)
  }, [open, denied, graceReachableBlocked, t])

  if (!open) return null

  const verifyWithPasskey = async () => {
    if (passkeyBusy) return
    setPasskeyBusy(true)
    setSyncedFail(false)
    try {
      const { options, challengeId } = await api.webauthnAuthBegin()
      const credential = await startAuthentication({
        optionsJSON: options as PublicKeyCredentialRequestOptionsJSON,
      })
      const result = await api.webauthnAuthComplete(challengeId, credential)
      if (result.deviceTrusted === true) {
        markDeviceApproved()
        recheckDeviceApproval()
        showToast({ type: 'success', message: t('deviceApproval.passkeyVerified') })
      } else {
        // #1114 A: 同期(multi_device)passkey で heal 不成立 → 恒久表示。
        setSyncedFail(true)
      }
    } catch (e) {
      const name = (e as { name?: string })?.name
      if (name === 'NotAllowedError' || name === 'AbortError') {
        showToast({ type: 'info', message: t('passkey.cancelled') })
      } else {
        console.error('[DeviceApproval] passkey verify failed:', e)
        showToast({ type: 'error', message: t('deviceApproval.passkeyFailed') })
      }
    } finally {
      setPasskeyBusy(false)
    }
  }

  const recaptchaAnchorId = denied ? 'device-approval-recaptcha-denied' : 'device-approval-recaptcha'

  const sendRecoverySms = async () => {
    if (smsBusy) return
    const e164 = toE164Jp(smsPhone)
    if (!e164) {
      showToast({ type: 'error', message: t('smsRecovery.phoneInvalid') })
      return
    }
    const user = auth.currentUser
    if (!user) {
      showToast({ type: 'error', message: t('deviceApproval.smsFailed') })
      return
    }
    setSmsBusy(true)
    try {
      await api.beginRecoverySms(e164)
      if (!verifierRef.current) {
        verifierRef.current = createInvisibleRecaptcha(recaptchaAnchorId)
      }
      confirmRef.current = await startPhoneReauth(user, e164, verifierRef.current)
      setSmsStep('code')
      showToast({ type: 'success', message: t('smsRecovery.codeSent') })
    } catch (e) {
      console.error('[DeviceApproval] sms begin failed:', e)
      if (e instanceof ApiError && e.status === 403) {
        showToast({ type: 'error', message: t('deviceApproval.smsMismatch') })
      } else if (e instanceof ApiError && e.status === 429) {
        showToast({ type: 'error', message: t('deviceApproval.smsRateLimited') })
      } else {
        showToast({ type: 'error', message: t('deviceApproval.smsFailed') })
      }
    } finally {
      setSmsBusy(false)
    }
  }

  const confirmRecoverySms = async () => {
    if (smsBusy || !confirmRef.current) return
    setSmsBusy(true)
    try {
      await confirmRef.current.confirm(smsCode.trim())
      await auth.currentUser?.getIdToken(true)
      const result = await api.trustDeviceViaSms(getDeviceId())
      if (result.trusted) {
        markDeviceApproved()
        recheckDeviceApproval()
        showToast({ type: 'success', message: t('deviceApproval.passkeyVerified') })
      }
    } catch (e) {
      console.error('[DeviceApproval] sms confirm failed:', e)
      const code = (e as { code?: string })?.code
      if (code === 'auth/invalid-verification-code' || code === 'auth/invalid-code') {
        showToast({ type: 'error', message: t('totp.invalidCode') })
      } else {
        showToast({ type: 'error', message: t('deviceApproval.smsFailed') })
      }
    } finally {
      setSmsBusy(false)
    }
  }

  // #1114: 退会・ログアウトは gate 中でも常時到達可能に。
  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }
  const handleDeleteAccount = async () => {
    if (!window.confirm(t('deviceApproval.deleteAccountConfirm'))) return
    try {
      await deleteAccount()
      showToast({ type: 'success', message: t('deviceApproval.accountDeleted') })
      navigate('/login', { replace: true })
    } catch (e) {
      console.error('[DeviceApproval] delete account failed:', e)
      showToast({ type: 'error', message: t('deviceApproval.actionError') })
    }
  }

  // SMS 復旧 UI(denied/未 denied 共通 = #1114 D)。
  const smsBlock = smsAvailable ? (
    <>
      {smsStep === 'idle' && (
        <button
          onClick={() => setSmsStep('phone')}
          className="w-full h-12 rounded-xl font-display font-bold text-sm border border-matcha text-matcha tap-effect transition-colors mb-2 flex items-center justify-center gap-2"
        >
          <MessageSquare size={18} />
          {t('deviceApproval.smsVerifyCta')}
        </button>
      )}
      {smsStep === 'phone' && (
        <div className="mb-2 rounded-xl border border-gray-200 p-3">
          <p className="font-body text-xs text-text-secondary mb-2">
            {t('deviceApproval.smsEnterPhone')}
          </p>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={smsPhone}
            onChange={(e) => setSmsPhone(e.target.value)}
            placeholder={t('smsRecovery.phonePlaceholder')}
            className="w-full h-11 mb-2 px-3 text-sm font-body border border-gray-300 rounded-xl focus:outline-none focus:border-yokoso-red"
          />
          <button
            onClick={sendRecoverySms}
            disabled={smsBusy || !smsPhone.trim()}
            className="w-full h-11 rounded-xl font-display font-bold text-sm bg-matcha text-white tap-effect disabled:opacity-50"
          >
            {smsBusy ? '...' : t('smsRecovery.sendCode')}
          </button>
        </div>
      )}
      {smsStep === 'code' && (
        <div className="mb-2 rounded-xl border border-gray-200 p-3">
          <p className="font-body text-xs text-text-secondary mb-2">
            {t('smsRecovery.codeInputLabel')}
          </p>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={smsCode}
            onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            className="w-full h-11 mb-2 text-center tracking-[0.4em] text-lg font-mono border border-gray-300 rounded-xl focus:outline-none focus:border-yokoso-red"
          />
          <button
            onClick={confirmRecoverySms}
            disabled={smsBusy || smsCode.length !== 6}
            className="w-full h-11 rounded-xl font-display font-bold text-sm bg-matcha text-white tap-effect disabled:opacity-50"
          >
            {smsBusy ? '...' : t('deviceApproval.smsConfirmAction')}
          </button>
        </div>
      )}
      <div id={recaptchaAnchorId} />
    </>
  ) : null

  // #1114: gate 中でも常時到達可能な退会/ログアウト導線。
  const escapeLinks = (
    <div className="mt-4 flex items-center justify-center gap-4">
      <button
        onClick={handleLogout}
        className="font-body text-[11px] text-text-secondary underline underline-offset-2 tap-effect"
      >
        {t('deviceApproval.logoutAction')}
      </button>
      <button
        onClick={handleDeleteAccount}
        className="font-body text-[11px] text-text-tertiary underline underline-offset-2 tap-effect"
      >
        {t('deviceApproval.deleteAccountAction')}
      </button>
    </div>
  )

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 animate-scale-in max-h-[90vh] overflow-y-auto">
        <div
          className={`flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full ${
            denied ? 'bg-amber-100' : 'bg-navy/10'
          }`}
        >
          {denied ? (
            <ShieldAlert size={32} className="text-amber-600" />
          ) : (
            <Smartphone size={32} className="text-navy" />
          )}
        </div>

        <h3 className="font-display text-xl font-bold text-text-primary text-center mb-2">
          {t(denied ? 'deviceApproval.deniedTitle' : 'deviceApproval.waitTitle')}
        </h3>
        <p className="font-body text-sm text-text-secondary text-center mb-5 leading-relaxed">
          {t(denied ? 'deviceApproval.deniedDescription' : 'deviceApproval.waitDescription')}
        </p>

        {denied ? (
          <>
            {/* #1114 D: 拒否端末も SMS(登録番号所持)で自己復旧できる。 */}
            {smsBlock}
            {smsAvailable && (
              <p className="font-body text-[11px] text-text-secondary text-center mt-1 leading-relaxed">
                {t('deviceApproval.deniedSmsHint')}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="inline-block w-2 h-2 rounded-full bg-navy animate-pulse" />
              <span className="font-body text-xs text-text-secondary text-center">
                {graceReachableBlocked
                  ? t('deviceApproval.graceReachableHint')
                  : graceRemaining !== null && graceRemaining > 0
                    ? t('deviceApproval.graceCountdown', { seconds: String(graceRemaining) })
                    : t('deviceApproval.waitChecking')}
              </span>
            </div>

            {/* #1114 A: 同期 passkey で自動信頼不可の恒久表示(トースト脱却)。 */}
            {syncedFail && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-3">
                <p className="text-xs font-body text-amber-900 leading-relaxed">
                  {t('deviceApproval.passkeySyncedPersistent')}
                </p>
              </div>
            )}

            {supported && (
              <button
                onClick={verifyWithPasskey}
                disabled={passkeyBusy}
                className="w-full h-12 rounded-xl font-display font-bold text-sm bg-matcha text-white tap-effect transition-colors disabled:opacity-50 mb-2 flex items-center justify-center gap-2"
              >
                <Fingerprint size={18} />
                {passkeyBusy
                  ? t('deviceApproval.passkeyBusy')
                  : t('deviceApproval.passkeyVerifyCta')}
              </button>
            )}

            {smsBlock}

            <button
              onClick={onRecheck}
              className="w-full h-12 rounded-xl font-display font-bold text-sm bg-yokoso-red text-white tap-effect transition-colors"
            >
              {t('deviceApproval.recheckAction')}
            </button>
          </>
        )}

        <p className="font-body text-[11px] text-text-secondary text-center mt-4 leading-relaxed">
          {t('deviceApproval.waitRecoveryHint')}
        </p>

        {escapeLinks}
      </div>
    </div>
  )
}
